import { useState, useEffect } from "react";
import {
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  Paper,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import Page from "src/components/Page";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

/**
 * 职级津贴配置页面
 * 配置主管/经理/总监三个职级的日业绩目标和津贴奖励
 */
export default function RoleAllowanceConfig() {
  const [config, setConfig] = useState({
    juniorTargetAmount: 30000,
    juniorAllowance: 50,
    middleTargetAmount: 300000,
    middleAllowance: 500,
    seniorTargetAmount: 3000000,
    seniorAllowance: 5000,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      const { data } = await asyncRequest({
        method: "post",
        path: "/system/config/list",
        data: {
          page: 0,
          size: 10,
          keys: [
            'roleAllowanceJuniorTarget',
            'roleAllowanceJuniorAllowance',
            'roleAllowanceMiddleTarget',
            'roleAllowanceMiddleAllowance',
            'roleAllowanceSeniorTarget',
            'roleAllowanceSeniorAllowance',
          ],
        },
      });

      if (data && data.length > 0) {
        const cfg = {};
        data.forEach(item => {
          cfg[item.key] = item.value;
        });

        setConfig({
          juniorTargetAmount: parseFloat(cfg.roleAllowanceJuniorTarget) || 30000,
          juniorAllowance: parseFloat(cfg.roleAllowanceJuniorAllowance) || 50,
          middleTargetAmount: parseFloat(cfg.roleAllowanceMiddleTarget) || 300000,
          middleAllowance: parseFloat(cfg.roleAllowanceMiddleAllowance) || 500,
          seniorTargetAmount: parseFloat(cfg.roleAllowanceSeniorTarget) || 3000000,
          seniorAllowance: parseFloat(cfg.roleAllowanceSeniorAllowance) || 5000,
        });
      }
    } catch (e) {
      console.error("获取职级津贴配置失败:", e);
    }
  }

  const handleConfigChange = (field, value) => {
    setConfig({
      ...config,
      [field]: parseFloat(value) || 0,
    });
  };

  const validateConfig = () => {
    if (config.juniorTargetAmount <= 0) {
      alert('主管的日业绩目标必须大于0');
      return false;
    }
    if (config.juniorAllowance < 0) {
      alert('主管的津贴奖励不能为负数');
      return false;
    }
    if (config.middleTargetAmount <= 0) {
      alert('经理的日业绩目标必须大于0');
      return false;
    }
    if (config.middleAllowance < 0) {
      alert('经理的津贴奖励不能为负数');
      return false;
    }
    if (config.seniorTargetAmount <= 0) {
      alert('总监的日业绩目标必须大于0');
      return false;
    }
    if (config.seniorAllowance < 0) {
      alert('总监的津贴奖励不能为负数');
      return false;
    }

    if (config.juniorTargetAmount >= config.middleTargetAmount) {
      alert('主管的日业绩目标必须小于经理');
      return false;
    }
    if (config.middleTargetAmount >= config.seniorTargetAmount) {
      alert('经理的日业绩目标必须小于总监');
      return false;
    }
    if (config.juniorAllowance >= config.middleAllowance) {
      alert('主管的津贴奖励必须小于经理');
      return false;
    }
    if (config.middleAllowance >= config.seniorAllowance) {
      alert('经理的津贴奖励必须小于总监');
      return false;
    }

    return true;
  };

  async function handleSave() {
    if (!validateConfig()) {
      return;
    }

    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "post",
        path: "/system/config/role-allowance/update",
        data: {
          roleAllowanceJuniorTarget: config.juniorTargetAmount,
          roleAllowanceJuniorAllowance: config.juniorAllowance,
          roleAllowanceMiddleTarget: config.middleTargetAmount,
          roleAllowanceMiddleAllowance: config.middleAllowance,
          roleAllowanceSeniorTarget: config.seniorTargetAmount,
          roleAllowanceSeniorAllowance: config.seniorAllowance,
        },
      });
      alert(msg || '保存成功', 'success');
      fetchConfig();
    } catch (e) {
      console.error("保存职级津贴配置失败:", e);
      alert(e?.message || e || '保存失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset() {
    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "post",
        path: "/system/config/role-allowance/update",
        data: {
          roleAllowanceJuniorTarget: 30000,
          roleAllowanceJuniorAllowance: 50,
          roleAllowanceMiddleTarget: 300000,
          roleAllowanceMiddleAllowance: 500,
          roleAllowanceSeniorTarget: 3000000,
          roleAllowanceSeniorAllowance: 5000,
        },
      });
      alert(msg || '重置成功', 'success');
      fetchConfig();
    } catch (e) {
      console.error("重置配置失败:", e);
      alert(e?.message || e || '重置失败');
    } finally {
      setLoading(false);
    }
  }

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Page title="职级津贴配置">
      <Container maxWidth="lg">
        <Stack spacing={3} sx={{ py: 3 }}>
          <Typography variant="h4">职级津贴配置</Typography>

          <Alert severity="info">
            <Typography variant="body2">
              配置说明：根据金银通职级（主管/经理/总监），设置每日小区业绩目标和对应的权益金津贴奖励。
              系统每日自动统计用户小区业绩，达标后自动发放对应津贴。
            </Typography>
          </Alert>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              当前配置
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>职级</TableCell>
                    <TableCell>日业绩目标（元）</TableCell>
                    <TableCell>津贴奖励（权益金）</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        主管
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.juniorTargetAmount}
                        onChange={(e) =>
                          handleConfigChange("juniorTargetAmount", e.target.value)
                        }
                        inputProps={{ step: 1000, min: 0 }}
                        sx={{ width: 200 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        ({formatMoney(config.juniorTargetAmount)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.juniorAllowance}
                        onChange={(e) =>
                          handleConfigChange("juniorAllowance", e.target.value)
                        }
                        inputProps={{ step: 10, min: 0 }}
                        sx={{ width: 150 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        权益金
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        经理
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.middleTargetAmount}
                        onChange={(e) =>
                          handleConfigChange("middleTargetAmount", e.target.value)
                        }
                        inputProps={{ step: 10000, min: 0 }}
                        sx={{ width: 200 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        ({formatMoney(config.middleTargetAmount)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.middleAllowance}
                        onChange={(e) =>
                          handleConfigChange("middleAllowance", e.target.value)
                        }
                        inputProps={{ step: 10, min: 0 }}
                        sx={{ width: 150 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        权益金
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        总监
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.seniorTargetAmount}
                        onChange={(e) =>
                          handleConfigChange("seniorTargetAmount", e.target.value)
                        }
                        inputProps={{ step: 100000, min: 0 }}
                        sx={{ width: 200 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        ({formatMoney(config.seniorTargetAmount)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={config.seniorAllowance}
                        onChange={(e) =>
                          handleConfigChange("seniorAllowance", e.target.value)
                        }
                        inputProps={{ step: 100, min: 0 }}
                        sx={{ width: 150 }}
                      />
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        权益金
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "保存中..." : "保存配置"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
              >
                重置为默认
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              配置示例
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>默认配置：</strong>
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 主管：日业绩目标 30,000 元 → 津贴奖励 50 权益金
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 经理：日业绩目标 300,000 元 → 津贴奖励 500 权益金
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 总监：日业绩目标 3,000,000 元 → 津贴奖励 5,000 权益金
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>业务规则：</strong>
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 系统每日自动统计用户小区业绩（取较小区业绩）
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 业绩达到对应职级目标时，自动发放津贴权益金
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                • 同一用户当天只能获得一次津贴（取最高达标职级）
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Page>
  );
}
